class StickyCarousel extends HTMLElement {
  constructor() {
    super();
    // Cache DOM elements once on initialization
    this.carousel = this.querySelector('.product-main-css-swiper');

    // Cache selectors to avoid repeated DOM queries
    this.stickyStartpoint = document.querySelector('.sticky-product-gallery--start');
    this.stickyEndpoint = document.querySelector('.sticky-product-gallery--end');
    this.stickyHeader = document.querySelector('sticky-header');
    this.sectionHeader = document.querySelector('.section-header');
    this.shopifyStickyHeader = document.querySelector('.shopify-section-header-sticky');

    // State flags
    this.isSticky = false;
    this.isAtEndpoint = false;
    this.previousScrollY = window.scrollY;

    // Bind methods to preserve context
    this.checkIfMobile = this.checkIfMobile.bind(this);
    this.handleScroll = this.handleScroll.bind(this);
    this.handleVariantChange = this.handleVariantChange.bind(this);
    this.handleProductInfoLoaded = this.handleProductInfoLoaded.bind(this);
    this.handleOptionChange = this.handleOptionChange.bind(this);

    // Flag to track if reinitialization is pending
    this.pendingReinit = false;

    // Flag to track if initial clones have been created
    this.initialClonesCreated = false;

    // Initialize clones if all required elements exist
    if (this.carousel && this.stickyStartpoint && this.stickyEndpoint) {
      // Set up event listeners with debounced resize handler using global debounce
      this.onResize = debounce(this.checkIfMobile, 250);
      window.addEventListener('resize', this.onResize);

      // Subscribe to option changes (fires BEFORE content updates) to remove clones early
      if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.optionChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, this.handleOptionChange);
        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.handleVariantChange);
      }

      // Listen for product info loaded to reinitialize after variant changes
      document.addEventListener('product-info:loaded', this.handleProductInfoLoaded);

      // Delay initial clone creation to avoid interfering with main gallery initialization
      // Wait for next tick to ensure css-swiper and thumbnails are fully initialized
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.createClones();
          this.initialClonesCreated = true;
          this.checkIfMobile();
        });
      });
    }
  }

  // Create clones of the carousel for sticky and endpoint positions
  createClones() {
    // Clean up existing scroll sync handlers
    this.cleanupScrollSync();

    // Clean up any existing clones first (in case this is called multiple times)
    if (this.stickyClone) {
      this.stickyClone.remove();
      this.stickyClone = null;
    }
    if (this.endpointClone) {
      this.endpointClone.remove();
      this.endpointClone = null;
    }

    // Also clear any orphaned clones from the containers
    this.stickyStartpoint.querySelectorAll('.sticky-carousel-clone').forEach((el) => el.remove());
    this.stickyEndpoint.querySelectorAll('.endpoint-carousel-clone').forEach((el) => el.remove());

    // Create sticky clone
    this.stickyClone = this.carousel.cloneNode(true);
    this.stickyClone.classList.add('sticky-carousel-clone');
    this.stickyClone.style.display = 'none';
    this.stickyClone.classList.remove('scroll-trigger');
    this.stickyClone.removeAttribute('id');
    this.stickyClone.querySelector('.css-swiper__button-wrapper')?.remove();

    // Remove AR viewers from the sticky clone BEFORE appending
    this.stickyClone.querySelectorAll('ar-view-360').forEach((el) => el.remove());

    // Remove variant thumbnails from sticky clone
    this.stickyClone.querySelector('.product-media-gallery__variant-thumbnails-wrapper')?.remove();

    // Remove loading class from lazy-images to hide loading spinner
    this.stickyClone.querySelectorAll('.lazy-image.loading, .product-media__wrapper.loading').forEach((el) => {
      el.classList.remove('loading');
    });

    this.#hideFromAssistiveTech(this.stickyClone);
    this.stickyStartpoint.appendChild(this.stickyClone);

    // Ensure sticky container is hidden initially (will be shown by makeSticky)
    this.stickyStartpoint.style.display = 'none';

    // Create endpoint clone
    this.endpointClone = this.carousel.cloneNode(true);
    this.endpointClone.classList.add('endpoint-carousel-clone');
    this.endpointClone.style.display = 'none';
    this.endpointClone.classList.remove('scroll-trigger');
    this.endpointClone.removeAttribute('id');
    this.endpointClone.querySelector('.css-swiper__button-wrapper')?.remove();

    // Remove AR viewers from the endpoint clone BEFORE appending
    this.endpointClone.querySelectorAll('ar-view-360').forEach((el) => el.remove());

    // Remove variant thumbnails from endpoint clone
    this.endpointClone.querySelector('.product-media-gallery__variant-thumbnails-wrapper')?.remove();

    // Remove loading class from lazy-images to hide loading spinner
    this.endpointClone.querySelectorAll('.lazy-image.loading, .product-media__wrapper.loading').forEach((el) => {
      el.classList.remove('loading');
    });

    this.#hideFromAssistiveTech(this.endpointClone);
    this.stickyEndpoint.appendChild(this.endpointClone);

    // Set up button relay for modal triggers
    this.setupButtonRelay();

    // Set up bidirectional scroll sync
    this.setupBidirectionalSync();

    this.loadMedia();

    // Remove loading state after clones are created (keep display: none)
    if (this.stickyStartpoint) {
      this.stickyStartpoint.style.minHeight = '';
      this.stickyStartpoint.classList.remove('loading', 'line-loader');
      // Note: Keep display as 'none' - makeSticky will show it when needed
    }
  }

  // Clean up scroll sync handlers
  cleanupScrollSync() {
    // Clear any pending sync timeout
    if (this.scrollSyncTimeout) {
      clearTimeout(this.scrollSyncTimeout);
      this.scrollSyncTimeout = null;
    }

    // Remove event listeners
    if (this.scrollSyncHandlers) {
      Object.values(this.scrollSyncHandlers).forEach(({ element, handler }) => {
        if (element && handler) {
          element.removeEventListener('scroll', handler);
        }
      });
      this.scrollSyncHandlers = null;
    }
  }

  // Set up bidirectional scroll sync between all carousels
  setupBidirectionalSync() {
    const mainCarousel = this.carousel.querySelector('ul[data-carousel]');
    const stickyCarousel = this.stickyClone.querySelector('ul[data-carousel]');
    const endpointCarousel = this.endpointClone.querySelector('ul[data-carousel]');

    if (!mainCarousel || !stickyCarousel || !endpointCarousel) return;

    // Track which carousel is being scrolled to avoid circular updates
    let isSyncing = false;

    // Sync function that runs AFTER scrolling ends (not during)
    const syncAfterScrollEnd = (sourceCarousel) => {
      // Clear any pending sync
      if (this.scrollSyncTimeout) {
        clearTimeout(this.scrollSyncTimeout);
      }

      // Wait for scroll to fully end before syncing
      this.scrollSyncTimeout = setTimeout(() => {
        if (isSyncing) return;
        isSyncing = true;

        const scrollPosition = sourceCarousel.scrollLeft;

        // Sync to other carousels instantly - like a blink, no smooth transition
        [mainCarousel, stickyCarousel, endpointCarousel].forEach((carousel) => {
          if (carousel !== sourceCarousel && carousel.offsetWidth > 0) {
            // Store original scroll behavior
            const originalScrollBehavior = carousel.style.scrollBehavior;

            // Force instant scroll (no smooth)
            carousel.style.scrollBehavior = 'auto';

            // Direct assignment for instant update (blink effect)
            carousel.scrollLeft = scrollPosition;

            // Restore original scroll behavior after a frame
            requestAnimationFrame(() => {
              carousel.style.scrollBehavior = originalScrollBehavior;
            });
          }
        });

        isSyncing = false;
        this.scrollSyncTimeout = null;
      }, 150); // Wait 150ms after last scroll event
    };

    // Add scroll listeners to all carousels
    // These only queue up a sync after scrolling stops
    const mainScrollHandler = () => syncAfterScrollEnd(mainCarousel);
    const stickyScrollHandler = () => syncAfterScrollEnd(stickyCarousel);
    const endpointScrollHandler = () => syncAfterScrollEnd(endpointCarousel);

    mainCarousel.addEventListener('scroll', mainScrollHandler, { passive: true });
    stickyCarousel.addEventListener('scroll', stickyScrollHandler, { passive: true });
    endpointCarousel.addEventListener('scroll', endpointScrollHandler, { passive: true });

    // Store handlers for cleanup
    this.scrollSyncHandlers = {
      main: { element: mainCarousel, handler: mainScrollHandler },
      sticky: { element: stickyCarousel, handler: stickyScrollHandler },
      endpoint: { element: endpointCarousel, handler: endpointScrollHandler },
    };
  }

  setupButtonRelay() {
    const selectors = '[name="viewer360"], [name="viewerRoom"], .zoom-button';
    // Set up relay for sticky clone
    this.stickyClone.querySelectorAll(selectors).forEach((cloneButton, index) => {
      cloneButton.addEventListener('click', (event) => {
        event.preventDefault();
        // Find the corresponding button by index in the main carousel
        const mainButtons = this.carousel.querySelectorAll(selectors);
        const mainButton = mainButtons[index];
        if (mainButton) {
          mainButton.click();
        }
      });
    });

    // Set up relay for endpoint clone
    this.endpointClone
      .querySelectorAll('[name="viewer360"], [name="viewerRoom"], .zoom-button')
      .forEach((cloneButton, index) => {
        cloneButton.addEventListener('click', (event) => {
          event.preventDefault();
          // Find the corresponding button by index in the main carousel
          const mainButtons = this.carousel.querySelectorAll('[name="viewer360"], [name="viewerRoom"], .zoom-button');
          const mainButton = mainButtons[index];
          if (mainButton) {
            mainButton.click();
          }
        });
      });
  }

  // Handle option changes - remove clones BEFORE content updates
  handleOptionChange() {
    // Capture current height before removing clones to prevent layout shift
    if (this.stickyStartpoint && this.stickyClone) {
      const currentHeight = this.stickyStartpoint.offsetHeight;
      if (currentHeight > 0) {
        this.stickyStartpoint.style.minHeight = `${currentHeight}px`;
      }
    }

    // Add loading classes and show the container
    if (this.stickyStartpoint) {
      this.stickyStartpoint.classList.add('loading', 'line-loader');
      this.stickyStartpoint.style.display = 'block';
    }

    // Immediately remove clones to prevent interference with gallery update
    if (this.stickyClone) {
      this.stickyClone.remove();
      this.stickyClone = null;
    }
    if (this.endpointClone) {
      this.endpointClone.remove();
      this.endpointClone = null;
    }
  }

  // Handle variant changes by completely unloading sticky carousel
  handleVariantChange() {
    // Remove scroll listener
    window.removeEventListener('scroll', this.handleScroll);

    // Completely remove clones from DOM
    if (this.stickyClone) {
      this.stickyClone.remove();
      this.stickyClone = null;
    }
    if (this.endpointClone) {
      this.endpointClone.remove();
      this.endpointClone = null;
    }

    // Reset all states
    this.isSticky = false;
    this.isAtEndpoint = false;
    this.classList.remove('has-sticky-carousel');
    this.classList.remove('carousel-at-endpoint');

    // Clear any pending syncs or reinitializations
    if (this.syncTimeout) {
      clearTimeout(this.syncTimeout);
      this.syncTimeout = null;
    }
    if (this.reinitTimeout) {
      clearTimeout(this.reinitTimeout);
    }

    // Set flag to reinitialize when product info finishes loading
    this.pendingReinit = true;
  }

  // Handle product info loaded event - reinitialize if pending
  handleProductInfoLoaded() {
    if (this.pendingReinit) {
      this.pendingReinit = false;

      // Small delay to ensure all images and lazy loading has initialized
      this.reinitTimeout = setTimeout(() => {
        this.reinitialize();
      }, 300);
    }
  }

  // Reinitialize sticky carousel with fresh content
  reinitialize() {
    // Re-query ALL DOM elements to get fresh references after variant change
    this.carousel = this.querySelector('.product-main-css-swiper');
    this.stickyStartpoint = document.querySelector('.sticky-product-gallery--start');
    this.stickyEndpoint = document.querySelector('.sticky-product-gallery--end');
    this.stickyHeader = document.querySelector('sticky-header');
    this.sectionHeader = document.querySelector('.section-header');
    this.shopifyStickyHeader = document.querySelector('.shopify-section-header-sticky');

    // Verify carousel exists
    if (!this.carousel) {
      // Clear loading state even if initialization fails (hide the container)
      if (this.stickyStartpoint) {
        this.stickyStartpoint.style.minHeight = '';
        this.stickyStartpoint.style.display = 'none';
        this.stickyStartpoint.classList.remove('loading', 'line-loader');
      }
      return;
    }

    // Verify carousel has content before cloning
    const carouselItems = this.carousel.querySelectorAll('[data-carousel-item]');
    if (carouselItems.length === 0) {
      // Clear loading state even if initialization fails (hide the container)
      if (this.stickyStartpoint) {
        this.stickyStartpoint.style.minHeight = '';
        this.stickyStartpoint.style.display = 'none';
        this.stickyStartpoint.classList.remove('loading', 'line-loader');
      }
      return;
    }

    // Verify sticky containers exist
    if (!this.stickyStartpoint || !this.stickyEndpoint) {
      // Clear loading state even if initialization fails (hide the container)
      if (this.stickyStartpoint) {
        this.stickyStartpoint.style.minHeight = '';
        this.stickyStartpoint.style.display = 'none';
        this.stickyStartpoint.classList.remove('loading', 'line-loader');
      }
      return;
    }

    // Clean up any existing clones from previous initialization
    if (this.stickyClone) {
      this.stickyClone.remove();
      this.stickyClone = null;
    }
    if (this.endpointClone) {
      this.endpointClone.remove();
      this.endpointClone = null;
    }

    // Create fresh clones from new variant content
    this.createClones();

    // Re-check if we're on mobile and set up scroll handler
    this.checkIfMobile();

    // Check current scroll position to see if we should already be sticky
    if (window.innerWidth <= 749) {
      this.handleScroll();
    }
  }

  // Check if we're on mobile and set up scroll handler accordingly
  checkIfMobile() {
    const isMobile = window.innerWidth <= 749;

    if (isMobile) {
      window.addEventListener('scroll', this.handleScroll);
    } else {
      window.removeEventListener('scroll', this.handleScroll);
      // Reset all states when switching to desktop
      if (this.isSticky || this.isAtEndpoint) {
        this.resetSticky();
      }
    }
  }

  // The main scroll handler - optimized
  handleScroll() {
    // Check header visibility state
    const headerVisibilityClass = 'shopify-section-header-hidden';
    const isHeaderHidden = this.shopifyStickyHeader?.classList.contains(headerVisibilityClass) || false;

    // Get current position
    const rect = this.getBoundingClientRect();
    const shouldBeSticky = rect.top <= 0;

    // Handle endpoint transition
    if (this.isSticky && this.stickyEndpoint) {
      const endpointRect = this.stickyEndpoint.getBoundingClientRect();
      const containerHeight = this.stickyStartpoint?.offsetHeight || 0;

      // Calculate distance to endpoint
      const totalDistanceToEndpoint = endpointRect.top - containerHeight;

      // Toggle endpoint state based on position
      if (totalDistanceToEndpoint <= 0 && !this.isAtEndpoint) {
        this.moveToEndpoint();
      } else if (!isHeaderHidden && this.isAtEndpoint) {
        const headerHeight = this.stickyHeader?.offsetHeight || 0;
        if (totalDistanceToEndpoint > headerHeight - 2) {
          this.resetFromEndpoint();
        }
      }
    }

    // Toggle sticky state
    if (
      !this.isSticky &&
      shouldBeSticky &&
      document.querySelector('.shopify-section-header-sticky') &&
      !this.isAtEndpoint
    ) {
      this.makeSticky();
    } else if (this.isSticky && !this.isAtEndpoint) {
      // Calculate reset threshold dynamically
      const headerHeight = this.stickyHeader?.offsetHeight || 48;
      const resetThreshold = headerHeight;

      // Only reset when sufficiently above the threshold
      if (rect.top > resetThreshold || document.querySelector('.shopify-section-header-sticky') === null) {
        this.resetSticky();
      }
    }
  }

  // Make the carousel sticky by showing the sticky clone
  makeSticky() {
    if (this.isSticky) return;

    // Safety check: don't proceed if clones don't exist yet
    if (!this.stickyClone || !this.endpointClone) return;

    // Show the sticky container and its clone
    this.stickyStartpoint.style.display = 'block';
    this.stickyClone.style.display = null;
    this.endpointClone.style.display = 'none';

    // Get scroll position after display change to ensure accurate reading
    const scrollLeft = this.carousel.querySelector('ul[data-carousel]')?.scrollLeft || 0;
    this.syncCarousel(scrollLeft);

    this.isSticky = true;
    this.classList.add('has-sticky-carousel');

    // Additional sync after a short delay to catch any layout shifts
    this.#ensureSync(scrollLeft);
  }

  // Move to the endpoint by showing the endpoint clone
  moveToEndpoint() {
    if (this.isAtEndpoint || !this.stickyEndpoint) return;

    // Safety check: don't proceed if clones don't exist yet
    if (!this.stickyClone || !this.endpointClone) return;

    const scrollLeft = this.stickyClone.querySelector('ul[data-carousel]')?.scrollLeft || 0;

    this.isAtEndpoint = true;
    // Hide the sticky container when moving to endpoint
    this.stickyStartpoint.style.display = 'none';
    this.stickyClone.style.display = 'none';
    this.endpointClone.style.display = null;

    // Sync endpoint to show the same image as sticky
    this.syncCarousel(scrollLeft);

    this.classList.add('carousel-at-endpoint');

    // Additional sync after a short delay to catch any layout shifts
    this.#ensureSync(scrollLeft);
  }

  // Reset from endpoint to sticky state
  resetFromEndpoint() {
    if (!this.isAtEndpoint) return;

    // Safety check: don't proceed if clones don't exist yet
    if (!this.stickyClone || !this.endpointClone) return;

    // Get current scroll position from endpoint
    const scrollLeft = this.endpointClone.querySelector('ul[data-carousel]')?.scrollLeft || 0;

    // Show the sticky container again when returning from endpoint
    this.stickyStartpoint.style.display = 'block';
    this.stickyClone.style.display = null;
    this.endpointClone.style.display = 'none';

    this.syncCarousel(scrollLeft);

    this.isAtEndpoint = false;
    this.classList.remove('carousel-at-endpoint');

    // Additional sync after a short delay to catch any layout shifts
    this.#ensureSync(scrollLeft);
  }

  // Reset from sticky state completely
  resetSticky() {
    if (!this.isSticky && !this.isAtEndpoint) return;

    // Safety check: don't proceed if clones don't exist yet
    if (!this.stickyClone || !this.endpointClone) return;

    // Reset from endpoint first if needed
    if (this.isAtEndpoint) {
      this.resetFromEndpoint();
      return; // resetFromEndpoint will handle the sync
    }

    const scrollLeft = this.stickyClone.querySelector('ul[data-carousel]')?.scrollLeft || 0;
    this.syncCarousel(scrollLeft);

    // Hide the sticky container and all clones
    this.stickyStartpoint.style.display = 'none';
    this.stickyClone.style.display = 'none';
    this.endpointClone.style.display = 'none';

    // Reset state
    this.isSticky = false;
    this.isAtEndpoint = false;
    this.classList.remove('has-sticky-carousel');

    // Additional sync after a short delay to catch any layout shifts
    this.#ensureSync(scrollLeft);
  }

  syncCarousel(scrollPosition) {
    const mainCarousel = this.carousel.querySelector('ul[data-carousel]');
    if (!mainCarousel) return;

    // Helper function to instantly sync without any smooth transition
    const instantSync = (carousel) => {
      if (!carousel || carousel.offsetWidth === 0) return;

      // Store and disable scroll behavior for instant update
      const originalScrollBehavior = carousel.style.scrollBehavior;
      carousel.style.scrollBehavior = 'auto';

      // Direct assignment - instant blink
      carousel.scrollLeft = scrollPosition;

      // Restore after a frame
      requestAnimationFrame(() => {
        carousel.style.scrollBehavior = originalScrollBehavior;
      });
    };

    // Sync all carousels instantly
    instantSync(mainCarousel);

    // Directly set clones in case they're hidden (scroll events won't fire)
    if (this.stickyClone) {
      const stickyCarousel = this.stickyClone.querySelector('ul[data-carousel]');
      instantSync(stickyCarousel);
    }

    if (this.endpointClone) {
      const endpointCarousel = this.endpointClone.querySelector('ul[data-carousel]');
      instantSync(endpointCarousel);
    }
  }

  #hideFromAssistiveTech(clone) {
    clone.setAttribute('aria-hidden', 'true');
    for (const el of clone.querySelectorAll('button, a, [tabindex]')) {
      el.setAttribute('tabindex', '-1');
    }
  }

  // Private method to ensure sync after layout shifts during page load
  #ensureSync(scrollPosition) {
    // Clear any existing timeout
    if (this.syncTimeout) {
      clearTimeout(this.syncTimeout);
    }

    // Retry sync after a short delay to handle layout shifts
    this.syncTimeout = setTimeout(() => {
      this.syncCarousel(scrollPosition);
    }, 100);
  }

  loadMedia() {
    const stickyClone = this.stickyClone.querySelector('ul[data-carousel]');
    const endpointClone = this.endpointClone.querySelector('ul[data-carousel]');

    // Get first 2 data-carousel-item excluding first item
    const stickyItems = Array.from(stickyClone.querySelectorAll('li[data-carousel-item]')).slice(1, 3);
    const endpointItems = Array.from(endpointClone.querySelectorAll('li[data-carousel-item]')).slice(1, 3);

    // Loop them and get the media by checking if it's an image or video and load it
    [stickyItems, endpointItems].forEach((items) => {
      items.forEach((item) => {
        const media = item.querySelector('img, video');
        // Check if it's an image or video and load it
        if (media.tagName === 'IMG') {
          media.loading = 'eager';
          media.src = media.src;
        } else if (media.tagName === 'VIDEO') {
          media.load();
          media.play();
        }
      });
    });
  }

  // Clean up event listeners when component is removed
  disconnectedCallback() {
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('scroll', this.handleScroll);
    document.removeEventListener('product-info:loaded', this.handleProductInfoLoaded);
    this.cleanupScrollSync();
    if (this.carousel) {
      this.carousel.removeEventListener('active-index-changed', this.handleActiveIndexChanged);
    }
    if (this.syncTimeout) {
      clearTimeout(this.syncTimeout);
    }
    if (this.reinitTimeout) {
      clearTimeout(this.reinitTimeout);
    }
    if (this.optionChangeUnsubscriber) {
      this.optionChangeUnsubscriber();
    }
    if (this.variantChangeUnsubscriber) {
      this.variantChangeUnsubscriber();
    }
  }
}

customElements.define('sticky-carousel', StickyCarousel);
